import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faChartLine } from '@fortawesome/pro-light-svg-icons/faChartLine';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { faImage } from '@fortawesome/pro-light-svg-icons/faImage';
import { faBolt } from '@fortawesome/pro-light-svg-icons/faBolt';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { PublishedPage } from './usePublishedPage';

export interface PublishedStep {
  id: string;
  icon: IconDefinition;
  title: string;
  description: string;
  /** A gap worth fixing now, drawn in the warning colour. */
  isWarning?: boolean;
  onClick: () => void;
}

interface UsePublishedStepsProps {
  accountId: number;
  smartLinkId: number;
  page: PublishedPage;
  closeModal: () => void;
}

/**
 * What is worth doing in the first minute, built from what is true of this
 * page.
 *
 * Every row is a fact we can check and a place we can reach: a bio link the
 * account really has, services the page really lacks, artwork it really has
 * not got. A row whose fact is missing is left out rather than filled with a
 * plausible sentence.
 */
export function usePublishedSteps({
  accountId,
  smartLinkId,
  page,
  closeModal,
}: UsePublishedStepsProps): PublishedStep[] {
  const intl = useIntl();
  const router = useSMLRouter();

  const goToCreator = useCallback(() => {
    closeModal();
    router.push(SMLRoutes.details, { accountId, smartLinkId });
  }, [closeModal, router, accountId, smartLinkId]);

  const goToStats = useCallback(() => {
    closeModal();
    router.push(SMLRoutes.manage, { accountId, smartLinkId });
  }, [closeModal, router, accountId, smartLinkId]);

  const goToBioLink = useCallback(() => {
    if (page.mainSmartLinkId === null) {
      return;
    }
    closeModal();
    router.push(SMLRoutes.details, { accountId, smartLinkId: page.mainSmartLinkId });
  }, [closeModal, router, accountId, page.mainSmartLinkId]);

  return useMemo(() => {
    const steps: PublishedStep[] = [];

    if (page.isPresave) {
      if (!page.hasCover) {
        steps.push({
          id: 'coverArt',
          icon: faImage,
          isWarning: true,
          title: intl.formatMessage({
            id: 'app.sml.published.step.cover.title',
            defaultMessage: 'Add cover art',
          }),
          description: intl.formatMessage({
            id: 'app.sml.published.step.cover.description',
            defaultMessage: 'The page has no artwork yet, and a story post of plain text is scrolled past.',
          }),
          onClick: goToCreator,
        });
      }
      steps.push({
        id: 'campaign',
        icon: faBolt,
        title: intl.formatMessage({
          id: 'app.sml.published.step.campaign.title',
          defaultMessage: 'Watch the campaign',
        }),
        description: intl.formatMessage({
          id: 'app.sml.published.step.campaign.description',
          defaultMessage: 'Pre-saves per service and email reminders, counted from the first fan.',
        }),
        onClick: goToStats,
      });
      return steps;
    }

    if (!page.isBioLink && page.bioLinkHost !== null && page.mainSmartLinkId !== null) {
      steps.push({
        id: 'bioLink',
        icon: faLink,
        title: intl.formatMessage({
          id: 'app.sml.published.step.bio.title',
          defaultMessage: 'Add it to your bio link',
        }),
        description: intl.formatMessage(
          {
            id: 'app.sml.published.step.bio.description',
            defaultMessage: 'Shows as a card on {host}, where most of your traffic already lands.',
          },
          { host: page.bioLinkHost }
        ),
        onClick: goToBioLink,
      });
    }

    steps.push({
      id: 'traffic',
      icon: faChartLine,
      title: intl.formatMessage({
        id: 'app.sml.published.step.traffic.title',
        defaultMessage: 'Watch the traffic',
      }),
      description: intl.formatMessage({
        id: 'app.sml.published.step.traffic.description',
        defaultMessage: 'Views, taps per service and where fans came from — from the first visit.',
      }),
      onClick: goToStats,
    });

    if (!page.isBioLink && page.missingServices.length > 0) {
      const names = intl.formatList(
        page.missingServices.map((service) => service.shortName),
        { type: 'conjunction' }
      );
      const topService = page.linkedServices[0];
      steps.push({
        id: 'missingServices',
        icon: faTriangleExclamation,
        isWarning: true,
        title: intl.formatMessage(
          {
            id: 'app.sml.published.step.services.title',
            defaultMessage: '{count, plural, one {# service is missing} other {# services are missing}}',
          },
          { count: page.missingServices.length }
        ),
        description:
          topService === undefined
            ? intl.formatMessage(
                {
                  id: 'app.sml.published.step.services.description_empty',
                  defaultMessage: '{names} are not linked, so fans on them find nothing to tap.',
                },
                { names }
              )
            : intl.formatMessage(
                {
                  id: 'app.sml.published.step.services.description',
                  defaultMessage: '{names} are not linked, so those fans get {topService} at the top instead.',
                },
                { names, topService: topService.shortName }
              ),
        onClick: goToCreator,
      });
    }

    return steps;
  }, [intl, page, goToCreator, goToStats, goToBioLink]);
}
